import type { ILanguageModel } from "@kuika/kuika-cl-model";
import type { ScreenDesignInstance } from "./designer-context";

/**
 * Locale-aware default copy for AI-authored, user-facing strings (validation
 * messages and action-confirmation dialogs).
 *
 * WHY this exists: Kuika user-facing strings are RAW TEXT in the project's
 * default language — the platform's translation system (`ILanguagePhrase`,
 * `isConfirmation`) sweeps them into the per-language phrase table at save time,
 * so the correct thing to author is text IN THE DEFAULT LANGUAGE, not a
 * translation key. The AI build previously hardcoded English, so a Turkish app
 * shipped English validation messages and a generic blue "Confirm" dialog.
 *
 * Locales: `tr` + `en` today, `en` as the fallback for any other default
 * language (strictly better than the previous always-English; a non-TR/EN app
 * keeps today's behavior). The tables are keyed so adding a locale is trivial.
 */
export type AiLocale = "tr" | "en";

/** Resolve the project's default language to a supported locale (fallback `en`). */
export function resolveProjectLocale(sdInstance: ScreenDesignInstance): AiLocale {
  const languages: ILanguageModel[] | undefined = sdInstance.projectProvider?.state?.project?.languages;
  const def: ILanguageModel | undefined = Array.isArray(languages) ? languages.find((l) => l.isDefault) : undefined;
  const short: string = (def?.shortName ?? "").trim().toLowerCase();
  if (short.startsWith("tr")) return "tr";
  return "en";
}

/**
 * Default validation error message for a rule, in the project's locale. The EN
 * wording is byte-identical to the previous hardcoded defaults so EN apps are
 * unchanged; TR is the localized equivalent.
 */
export function getValidationMessage(type: string, value: string | number | undefined, locale: AiLocale): string {
  const v: string | number = value ?? "";
  if (locale === "tr") {
    switch (type) {
      case "required":
        return "Bu alan zorunludur";
      case "minLength":
        return `En az ${v} karakter girilmelidir`;
      case "maxLength":
        return `En fazla ${v} karakter girilebilir`;
      case "minValue":
        return `En az ${v} olmalıdır`;
      case "maxValue":
        return `En fazla ${v} olabilir`;
      default:
        return "Geçersiz değer";
    }
  }
  switch (type) {
    case "required":
      return "This field is required";
    case "minLength":
      return `Minimum length is ${v}`;
    case "maxLength":
      return `Maximum length is ${v}`;
    case "minValue":
      return `Minimum value is ${v}`;
    case "maxValue":
      return `Maximum value is ${v}`;
    default:
      return "Validation failed";
  }
}

/** Semantic kind of confirmation — drives icon, color and copy. */
export type ConfirmationKind = "delete" | "default";

export interface ConfirmationDefaults {
  header: string;
  message: string;
  /** One of the picker's icon values (confirmation/warning/trash/info/question/notification). */
  icon: string;
  /** Freeform hex applied as the dialog icon's background gradient base. */
  iconBackgroundColor: string;
}

/**
 * Context-aware, localized defaults for an action confirmation dialog. A
 * destructive `delete` gets a trash icon + red background + an "irreversible"
 * warning; a benign confirm gets the neutral check + blue.
 */
export function getConfirmationDefaults(kind: ConfirmationKind, locale: AiLocale): ConfirmationDefaults {
  if (kind === "delete") {
    return {
      icon: "trash",
      iconBackgroundColor: "#DC2626",
      header: locale === "tr" ? "Kaydı Sil" : "Delete record",
      message:
        locale === "tr"
          ? "Bu kaydı silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
          : "Are you sure you want to delete this record? This action cannot be undone."
    };
  }
  return {
    icon: "confirmation",
    iconBackgroundColor: "#246dff",
    header: locale === "tr" ? "Onay" : "Confirmation",
    message: locale === "tr" ? "Bu işlemi yapmak istediğinize emin misiniz?" : "Are you sure you want to proceed?"
  };
}
